import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Coins,
  Globe,
  Link2,
  Lock,
  Mail,
  MapPin,
  Plus,
  Smartphone,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import RecruitmentShell from "../components/RecruitmentShell";
import RecruitmentProfileSettingsNav from "../components/RecruitmentProfileSettingsNav";
import { SocialBrandIcon, socialUrlForLink } from "../components/ProfileSocialBrand";
import {
  COUNTRY_DIAL_OPTIONS,
  getCountryIsoFromDial,
  getDialCodeForCountryIso,
} from "../countryDialOptions";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import companyLogoPlaceholder from "../assets/company-logo-placeholder.svg";
import WorkCountriesPillPicker from "../components/WorkCountriesPillPicker";
import { APPLICANT_COUNTRY_GROUPS } from "../data/applicantCountryGroups";
import { COUNTRIES } from "../data/countriesList";
import {
  deleteHiringCompanyLogo,
  deleteHiringProfilePhoto,
  getAuthMe,
  getEmailChangeLinkRequestEligibility,
  getMobileChangeLinkRequestEligibility,
  isApiConfigured,
  patchHiringRecruitmentDefaults,
  patchHiringSocialProfiles,
  postRequestEmailChange,
  postRequestMobileChange,
  postHiringCompanyLogo,
  postHiringProfilePhoto,
} from "../apiClient";
import { PROFILE_PHOTO_MAX_BYTES, PROFILE_PHOTO_MAX_MB } from "../config";
import { SALARY_CURRENCY_OPTIONS } from "./applicantData";

const MAX_CUSTOM_SOCIAL_LINKS = 3;
const MAX_DEFAULT_APPLY_COUNTRY_CODES = 50;
const ISO_SET = new Set(COUNTRIES.map((c) => c.value));
const EEA_COUNTRY_GROUP = APPLICANT_COUNTRY_GROUPS.find((g) => g.id === "EEA") ?? null;
const SALARY_CURRENCY_VALUE_SET = new Set(SALARY_CURRENCY_OPTIONS.map((o) => o.value));

function formatLocaleDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function isHiringRole(user) {
  return user?.role === "hiring-manager" || user?.role === "recruiter";
}

function makeRowId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rs-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyCustomSocialRow() {
  return { id: makeRowId(), platformName: "", url: "" };
}

function stripHttpScheme(raw) {
  return String(raw || "").replace(/^\s*https?:\/\//i, "").trimStart();
}

/** Restore custom platform rows from saved recruitment profile (same shape as applicant socialProfiles.customs). */
function normalizeRecruitmentCustomSocialRows(rp) {
  const s = rp?.socialProfiles;
  if (!s || typeof s !== "object") return [];
  if (Array.isArray(s.customs)) {
    return s.customs.slice(0, MAX_CUSTOM_SOCIAL_LINKS).map((row) => ({
      id: typeof row?.id === "string" && row.id ? row.id : makeRowId(),
      platformName: String(row?.platformName ?? row?.name ?? "").trim(),
      url: stripHttpScheme(String(row?.url ?? row?.link ?? "").trim()),
    }));
  }
  const legacyLabel = typeof s.customLabel === "string" ? s.customLabel.trim() : "";
  const legacyUrl = stripHttpScheme(typeof s.customUrl === "string" ? s.customUrl.trim() : "");
  if (legacyLabel || legacyUrl) {
    return [{ id: "legacy-custom", platformName: legacyLabel, url: legacyUrl }];
  }
  return [];
}

function readOfficePhoneFromRp(rp, fallbackDialCode = "+44") {
  const dialRaw = rp?.officePhoneDialCode;
  const dial =
    typeof dialRaw === "string" && dialRaw.trim() ? dialRaw.trim() : fallbackDialCode;
  let num = typeof rp?.officePhoneNumber === "string" ? rp.officePhoneNumber : "";
  if (!String(num).trim() && typeof rp?.officePhone === "string" && rp.officePhone.trim()) {
    num = rp.officePhone.trim();
  }
  return { officePhoneDialCode: dial, officePhoneNumber: num };
}

function emptyRecruitmentProfile() {
  return {
    organizationName: "",
    contactName: "",
    workEmail: "",
    careersUrl: "",
    officePhoneDialCode: "+44",
    officePhoneNumber: "",
    companyLogoDataUrl: "",
    socialProfiles: { linkedin: "", instagram: "", youtube: "" },
    defaultApplyAudience: "anyone",
    defaultApplyCountryCodes: [],
    defaultAdvertisingCurrency: "",
    defaultJobCountry: "",
    defaultJobTownCity: "",
  };
}

function readDefaultApplyAudience(rp) {
  const v = rp?.defaultApplyAudience;
  return v === "countries" ? "countries" : "anyone";
}

function readDefaultApplyCountryCodes(rp) {
  const a = rp?.defaultApplyCountryCodes;
  if (!Array.isArray(a)) return [];
  const out = [];
  const seen = new Set();
  for (const item of a) {
    const c = String(item || "").trim().toUpperCase();
    if (!ISO_SET.has(c) || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
    if (out.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) break;
  }
  return out;
}

function readDefaultAdvertisingCurrency(rp) {
  const raw = rp?.defaultAdvertisingCurrency;
  if (raw === null || raw === undefined) return "";
  const c = String(raw).trim().toUpperCase();
  return SALARY_CURRENCY_VALUE_SET.has(c) ? c : "";
}

function readRecruitmentSocial(rp) {
  const sp = rp?.socialProfiles;
  if (!sp || typeof sp !== "object") {
    return { linkedin: "", instagram: "", youtube: "" };
  }
  return {
    linkedin: stripHttpScheme(typeof sp.linkedin === "string" ? sp.linkedin : ""),
    instagram: stripHttpScheme(typeof sp.instagram === "string" ? sp.instagram : ""),
    youtube: stripHttpScheme(typeof sp.youtube === "string" ? sp.youtube : ""),
  };
}

function readSocialFromUser(user) {
  const sp = user?.socialProfiles;
  if (!sp || typeof sp !== "object") {
    return { linkedin: "", instagram: "", youtube: "" };
  }
  return {
    linkedin: stripHttpScheme(typeof sp.linkedin === "string" ? sp.linkedin : ""),
    instagram: stripHttpScheme(typeof sp.instagram === "string" ? sp.instagram : ""),
    youtube: stripHttpScheme(typeof sp.youtube === "string" ? sp.youtube : ""),
  };
}

function readRecruitmentProfile(user) {
  const rp = user?.recruitmentProfile;
  const base = emptyRecruitmentProfile();
  const fromTop =
    typeof user?.companyLogoDataUrl === "string" && user.companyLogoDataUrl ? user.companyLogoDataUrl : "";
  const fallbackCountryDial =
    typeof user?.countryCode === "string" && user.countryCode.trim() ? user.countryCode.trim() : "+44";
  const fallbackMobile = typeof user?.mobile === "string" ? user.mobile : "";
  const fallbackCompanyName = typeof user?.companyName === "string" ? user.companyName : "";
  const fallbackCompanyWebsite =
    typeof user?.companyWebsite === "string" ? stripHttpScheme(user.companyWebsite) : "";
  if (!rp || typeof rp !== "object") {
    return {
      ...base,
      organizationName: fallbackCompanyName,
      careersUrl: fallbackCompanyWebsite,
      officePhoneDialCode: fallbackCountryDial,
      officePhoneNumber: fallbackMobile,
      companyLogoDataUrl: fromTop,
      defaultApplyAudience: readDefaultApplyAudience(rp),
      defaultApplyCountryCodes: readDefaultApplyCountryCodes(rp),
      defaultAdvertisingCurrency: readDefaultAdvertisingCurrency(rp),
      defaultJobCountry: "",
      defaultJobTownCity: "",
    };
  }
  const fromRp = typeof rp.companyLogoDataUrl === "string" ? rp.companyLogoDataUrl : "";
  const office = readOfficePhoneFromRp(rp, fallbackCountryDial);
  return {
    organizationName:
      typeof rp.organizationName === "string" && rp.organizationName.trim()
        ? rp.organizationName
        : fallbackCompanyName,
    contactName: typeof rp.contactName === "string" ? rp.contactName : "",
    workEmail: typeof rp.workEmail === "string" ? rp.workEmail : "",
    careersUrl:
      typeof rp.careersUrl === "string" && rp.careersUrl.trim()
        ? stripHttpScheme(rp.careersUrl)
        : fallbackCompanyWebsite,
    officePhoneDialCode: office.officePhoneDialCode || fallbackCountryDial,
    officePhoneNumber: office.officePhoneNumber || fallbackMobile,
    companyLogoDataUrl: fromRp || fromTop,
    socialProfiles: readRecruitmentSocial(rp),
    defaultApplyAudience: readDefaultApplyAudience(rp),
    defaultApplyCountryCodes: readDefaultApplyCountryCodes(rp),
    defaultAdvertisingCurrency: readDefaultAdvertisingCurrency(rp),
    defaultJobCountry:
      typeof rp.defaultJobCountry === "string" && ISO_SET.has(rp.defaultJobCountry.trim().toUpperCase())
        ? rp.defaultJobCountry.trim().toUpperCase()
        : "",
    defaultJobTownCity:
      typeof rp.defaultJobTownCity === "string" ? String(rp.defaultJobTownCity).trim() : "",
  };
}

function fingerprintRecruitmentSettingsForm({
  defaultApplyAudience,
  defaultApplyCountryCodes,
  defaultAdvertisingCurrency,
  defaultJobCountry,
  defaultJobTownCity,
}) {
  return JSON.stringify({
    defaultApplyAudience,
    defaultApplyCountryCodes: [...defaultApplyCountryCodes].sort(),
    defaultAdvertisingCurrency,
    defaultJobCountry: String(defaultJobCountry || "").trim().toUpperCase(),
    defaultJobTownCity: String(defaultJobTownCity || "").trim(),
  });
}

function readOperatingCountryFromUser(user) {
  const raw = user?.defaultOperatingCountry ?? user?.countryOfResidence;
  const c = typeof raw === "string" ? raw.trim().toUpperCase() : "";
  return ISO_SET.has(c) ? c : "";
}

function fingerprintRecruitmentSettingsFromUser(user) {
  const rp = readRecruitmentProfile(user);
  return fingerprintRecruitmentSettingsForm({
    defaultApplyAudience: rp.defaultApplyAudience,
    defaultApplyCountryCodes: rp.defaultApplyCountryCodes,
    defaultAdvertisingCurrency: readDefaultAdvertisingCurrency(rp),
    defaultJobCountry: rp.defaultJobCountry,
    defaultJobTownCity: rp.defaultJobTownCity,
  });
}

function fingerprintRecruitmentManageForm({
  organizationName,
  contactName,
  workEmail,
  careersUrl,
  officePhoneCountryIso,
  officePhoneNumber,
  operatingCountry,
  socialLinkedin,
  socialInstagram,
  socialYoutube,
  customSocialRows,
  photoPreview,
  companyLogoPreview,
}) {
  const customs = customSocialRows
    .slice(0, MAX_CUSTOM_SOCIAL_LINKS)
    .map(({ id, platformName, url }) => ({
      id,
      platformName: (platformName || "").trim(),
      url: (url || "").trim(),
    }))
    .filter((row) => row.platformName || row.url);

  return JSON.stringify({
    organizationName: organizationName.trim(),
    contactName: contactName.trim(),
    workEmail: workEmail.trim(),
    careersUrl: careersUrl.trim(),
    officePhoneDialCode: getDialCodeForCountryIso(officePhoneCountryIso),
    officePhoneNumber: officePhoneNumber.trim(),
    operatingCountry: String(operatingCountry || "").trim().toUpperCase(),
    socialProfiles: {
      linkedin: socialLinkedin.trim(),
      instagram: socialInstagram.trim(),
      youtube: socialYoutube.trim(),
      customs,
    },
    photo: (photoPreview || "").trim(),
    companyLogo: (companyLogoPreview || "").trim(),
  });
}

function fingerprintFromCurrentHiringUser(user) {
  if (!user) return "";
  const rp = readRecruitmentProfile(user);
  const s = rp.socialProfiles;
  const topSocial = readSocialFromUser(user);
  return fingerprintRecruitmentManageForm({
    organizationName: rp.organizationName,
    contactName: rp.contactName || user.name || "",
    workEmail: rp.workEmail || user.email || "",
    careersUrl: rp.careersUrl,
    officePhoneCountryIso: getCountryIsoFromDial(rp.officePhoneDialCode || "+44") || "GB",
    officePhoneNumber: rp.officePhoneNumber || "",
    operatingCountry: readOperatingCountryFromUser(user),
    socialLinkedin: topSocial.linkedin || s.linkedin,
    socialInstagram: topSocial.instagram || s.instagram,
    socialYoutube: topSocial.youtube || s.youtube,
    customSocialRows: normalizeRecruitmentCustomSocialRows(user.recruitmentProfile),
    photoPreview: user.photoDataUrl || "",
    companyLogoPreview: rp.companyLogoDataUrl || "",
  });
}

function RecruitmentProfilePage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const photoInputRef = useRef(null);
  const pendingPhotoFileRef = useRef(null);
  const companyLogoInputRef = useRef(null);
  const pendingCompanyLogoFileRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [organizationName, setOrganizationName] = useState("");
  const [contactName, setContactName] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [careersUrl, setCareersUrl] = useState("");
  const [officePhoneCountryIso, setOfficePhoneCountryIso] = useState("GB");
  const [officePhoneNumber, setOfficePhoneNumber] = useState("");
  const [socialLinkedin, setSocialLinkedin] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [customSocialRows, setCustomSocialRows] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [activeSettingsTab, setActiveSettingsTab] = useState(() =>
    searchParams.get("tab") === "recruitment" ? "recruitment" : "profile"
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "recruitment") {
      setActiveSettingsTab("recruitment");
    } else if (tab === "profile" || tab === null || tab === "") {
      setActiveSettingsTab("profile");
    }
  }, [searchParams]);

  const selectSettingsTab = useCallback(
    (tab) => {
      setActiveSettingsTab(tab);
      if (tab === "recruitment") {
        setSearchParams({ tab: "recruitment" }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams]
  );
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);
  const [emailChangeEligibility, setEmailChangeEligibility] = useState(null);
  const [mobileChangeBusy, setMobileChangeBusy] = useState(false);
  const [mobileChangeEligibility, setMobileChangeEligibility] = useState(null);
  const [defaultApplyAudience, setDefaultApplyAudience] = useState("anyone");
  const [defaultApplyCountryCodes, setDefaultApplyCountryCodes] = useState([]);
  const [defaultApplyCountriesInputDraft, setDefaultApplyCountriesInputDraft] = useState("");
  const [defaultAdvertisingCurrency, setDefaultAdvertisingCurrency] = useState("");
  const [operatingCountry, setOperatingCountry] = useState("");
  const [defaultJobCountry, setDefaultJobCountry] = useState("");
  const [defaultJobTownCity, setDefaultJobTownCity] = useState("");

  const displayPhoto = photoPreview || profilePlaceholder;
  const displayCompanyLogo = companyLogoPreview || companyLogoPlaceholder;

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  }, []);

  const pushToast = useCallback((message, tone = "success") => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast({ message, tone });
    if (tone === "success") {
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null);
        toastTimerRef.current = null;
      }, 5500);
    }
  }, []);

  const isProfileDirty = useMemo(() => {
    if (!currentUser) return false;
    if (pendingPhotoFileRef.current || pendingCompanyLogoFileRef.current) return true;
    const baseline = fingerprintFromCurrentHiringUser(currentUser);
    const now = fingerprintRecruitmentManageForm({
      organizationName,
      contactName,
      workEmail,
      careersUrl,
      officePhoneCountryIso,
      officePhoneNumber,
      operatingCountry,
      socialLinkedin,
      socialInstagram,
      socialYoutube,
      customSocialRows,
      photoPreview: photoPreview || "",
      companyLogoPreview: companyLogoPreview || "",
    });
    return now !== baseline;
  }, [
    currentUser,
    organizationName,
    contactName,
    workEmail,
    careersUrl,
    officePhoneCountryIso,
    officePhoneNumber,
    operatingCountry,
    socialLinkedin,
    socialInstagram,
    socialYoutube,
    customSocialRows,
    photoPreview,
    companyLogoPreview,
  ]);

  const isRecruitmentSettingsDirty = useMemo(() => {
    if (!currentUser) return false;
    const baseline = fingerprintRecruitmentSettingsFromUser(currentUser);
    const now = fingerprintRecruitmentSettingsForm({
      defaultApplyAudience,
      defaultApplyCountryCodes,
      defaultAdvertisingCurrency,
      defaultJobCountry,
      defaultJobTownCity,
    });
    return now !== baseline;
  }, [
    currentUser,
    defaultApplyAudience,
    defaultApplyCountryCodes,
    defaultAdvertisingCurrency,
    defaultJobCountry,
    defaultJobTownCity,
  ]);

  const resetRecruitmentFormToSaved = useCallback(() => {
    if (!currentUser) return;
    pendingPhotoFileRef.current = null;
    pendingCompanyLogoFileRef.current = null;
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
    setPhotoPreview(currentUser.photoDataUrl || null);
    const rp = readRecruitmentProfile(currentUser);
    setCompanyLogoPreview(rp.companyLogoDataUrl ? rp.companyLogoDataUrl : null);
    setOrganizationName(rp.organizationName);
    setContactName(rp.contactName || currentUser.name || "");
    setWorkEmail(rp.workEmail || currentUser.email || "");
    setCareersUrl(rp.careersUrl);
    setOfficePhoneCountryIso(getCountryIsoFromDial(rp.officePhoneDialCode || "+44") || "GB");
    setOfficePhoneNumber(rp.officePhoneNumber || "");
    const s = rp.socialProfiles;
    const topSocial = readSocialFromUser(currentUser);
    setSocialLinkedin(topSocial.linkedin || s.linkedin);
    setSocialInstagram(topSocial.instagram || s.instagram);
    setSocialYoutube(topSocial.youtube || s.youtube);
    setCustomSocialRows(normalizeRecruitmentCustomSocialRows(currentUser.recruitmentProfile));
    const rpApply = readRecruitmentProfile(currentUser);
    setDefaultApplyAudience(rpApply.defaultApplyAudience);
    setDefaultApplyCountryCodes(rpApply.defaultApplyCountryCodes);
    setDefaultApplyCountriesInputDraft("");
    setDefaultAdvertisingCurrency(readDefaultAdvertisingCurrency(rpApply));
    setOperatingCountry(readOperatingCountryFromUser(currentUser));
    setDefaultJobCountry(rpApply.defaultJobCountry || "");
    setDefaultJobTownCity(rpApply.defaultJobTownCity || "");
  }, [currentUser]);

  const resetRecruitmentSettingsToSaved = useCallback(() => {
    if (!currentUser) return;
    const rpApply = readRecruitmentProfile(currentUser);
    setDefaultApplyAudience(rpApply.defaultApplyAudience);
    setDefaultApplyCountryCodes(rpApply.defaultApplyCountryCodes);
    setDefaultApplyCountriesInputDraft("");
    setDefaultAdvertisingCurrency(readDefaultAdvertisingCurrency(rpApply));
    setDefaultJobCountry(rpApply.defaultJobCountry || "");
    setDefaultJobTownCity(rpApply.defaultJobTownCity || "");
  }, [currentUser]);

  /** Refresh session from DB so recruitment defaults (including default job location) match `users` table. */
  useEffect(() => {
    if (!currentUser || !isHiringRole(currentUser)) return;
    const token = String(authToken || "").trim();
    if (!isApiConfigured() || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const { user } = await getAuthMe(token);
        if (cancelled || !user) return;
        setCurrentUser((prev) => {
          if (!prev || String(prev.id) !== String(user.id)) return prev;
          return { ...prev, ...user };
        });
      } catch {
        /* Offline or expired session — keep cached user */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, authToken, setCurrentUser]);

  useEffect(() => {
    if (!currentUser) return;
    pendingPhotoFileRef.current = null;
    pendingCompanyLogoFileRef.current = null;
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
    setPhotoPreview(currentUser.photoDataUrl || null);
    const rp = readRecruitmentProfile(currentUser);
    setCompanyLogoPreview(rp.companyLogoDataUrl ? rp.companyLogoDataUrl : null);
    setOrganizationName(rp.organizationName);
    setContactName(rp.contactName || currentUser.name || "");
    setWorkEmail(rp.workEmail || currentUser.email || "");
    setCareersUrl(rp.careersUrl);
    setOfficePhoneCountryIso(getCountryIsoFromDial(rp.officePhoneDialCode || "+44") || "GB");
    setOfficePhoneNumber(rp.officePhoneNumber || "");
    const s = rp.socialProfiles;
    const topSocial = readSocialFromUser(currentUser);
    setSocialLinkedin(topSocial.linkedin || s.linkedin);
    setSocialInstagram(topSocial.instagram || s.instagram);
    setSocialYoutube(topSocial.youtube || s.youtube);
    setCustomSocialRows(normalizeRecruitmentCustomSocialRows(currentUser.recruitmentProfile));
    const rpApply = readRecruitmentProfile(currentUser);
    setDefaultApplyAudience(rpApply.defaultApplyAudience);
    setDefaultApplyCountryCodes(rpApply.defaultApplyCountryCodes);
    setDefaultApplyCountriesInputDraft("");
    setDefaultAdvertisingCurrency(readDefaultAdvertisingCurrency(rpApply));
    setOperatingCountry(readOperatingCountryFromUser(currentUser));
    setDefaultJobCountry(rpApply.defaultJobCountry || "");
    setDefaultJobTownCity(rpApply.defaultJobTownCity || "");
  }, [currentUser]);

  const addCustomSocialRow = () => {
    setCustomSocialRows((rows) => {
      if (rows.length >= MAX_CUSTOM_SOCIAL_LINKS) return rows;
      return [...rows, emptyCustomSocialRow()];
    });
  };

  const removeCustomSocialRow = (index) => {
    setCustomSocialRows((rows) => rows.filter((_, i) => i !== index));
  };

  const updateCustomSocialRow = (index, field, value) => {
    setCustomSocialRows((rows) => {
      const next = [...rows];
      if (!next[index]) return rows;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const onPhotoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      pushToast(`Image is too large (max ${PROFILE_PHOTO_MAX_MB} MB).`, "error");
      if (photoInputRef.current) photoInputRef.current.value = "";
      return;
    }
    pendingPhotoFileRef.current = file;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(String(reader.result));
    };
    reader.readAsDataURL(file);
  }, [pushToast]);

  const handleProfilePhotoDelete = useCallback(() => {
    if (!photoPreview) return;
    pendingPhotoFileRef.current = null;
    if (photoInputRef.current) photoInputRef.current.value = "";

    const persistedHttp =
      currentUser?.photoDataUrl && !String(currentUser.photoDataUrl).startsWith("data:");
    const previewIsData = String(photoPreview).startsWith("data:");

    if (previewIsData) {
      if (persistedHttp) {
        setPhotoPreview(currentUser.photoDataUrl);
      } else {
        setPhotoPreview(null);
      }
      return;
    }

    setPhotoPreview(null);
  }, [currentUser, photoPreview]);

  const handleCompanyLogoDelete = useCallback(() => {
    pendingCompanyLogoFileRef.current = null;
    setCompanyLogoPreview(null);
    if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
  }, []);

  const onCompanyLogoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      pushToast(`Image is too large (max ${PROFILE_PHOTO_MAX_MB} MB).`, "error");
      if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
      return;
    }
    pendingCompanyLogoFileRef.current = file;
    const reader = new FileReader();
    reader.onload = () => {
      setCompanyLogoPreview(String(reader.result));
    };
    reader.readAsDataURL(file);
  }, [pushToast]);

  const loadEmailChangeEligibility = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!isApiConfigured() || !t) {
      setEmailChangeEligibility(null);
      return;
    }
    try {
      const data = await getEmailChangeLinkRequestEligibility(t);
      if (data && typeof data === "object") {
        setEmailChangeEligibility(data);
      } else {
        setEmailChangeEligibility(null);
      }
    } catch {
      setEmailChangeEligibility(null);
    }
  }, [authToken]);

  const loadMobileChangeEligibility = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!isApiConfigured() || !t) {
      setMobileChangeEligibility(null);
      return;
    }
    try {
      const data = await getMobileChangeLinkRequestEligibility(t);
      if (data && typeof data === "object") {
        setMobileChangeEligibility(data);
      } else {
        setMobileChangeEligibility(null);
      }
    } catch {
      setMobileChangeEligibility(null);
    }
  }, [authToken]);

  useEffect(() => {
    loadEmailChangeEligibility();
    loadMobileChangeEligibility();
  }, [loadEmailChangeEligibility, loadMobileChangeEligibility]);

  useEffect(() => {
    if (!emailChangeEligibility || emailChangeEligibility.canRequest) return undefined;
    const id = window.setInterval(() => {
      loadEmailChangeEligibility();
    }, 30000);
    return () => window.clearInterval(id);
  }, [emailChangeEligibility, loadEmailChangeEligibility]);

  useEffect(() => {
    if (!mobileChangeEligibility || mobileChangeEligibility.canRequest) return undefined;
    const id = window.setInterval(() => {
      loadMobileChangeEligibility();
    }, 30000);
    return () => window.clearInterval(id);
  }, [mobileChangeEligibility, loadMobileChangeEligibility]);

  const handleRequestEmailChange = useCallback(async () => {
    if (!isApiConfigured()) {
      pushToast("Connect the app to the API to change your email.", "error");
      return;
    }
    const t = String(authToken || "").trim();
    if (!t) {
      pushToast("Sign in again to change your email.", "error");
      return;
    }
    setEmailChangeBusy(true);
    try {
      const data = await postRequestEmailChange(t);
      if (data.skipped) {
        pushToast(data.message || "Email could not be sent.", "error");
      } else if (data.sent) {
        if (data.eligibility) {
          setEmailChangeEligibility(data.eligibility);
        } else {
          await loadEmailChangeEligibility();
        }
      } else {
        pushToast(data.message || "We could not send the email. Try again shortly.", "error");
      }
    } catch (err) {
      pushToast(err instanceof Error ? err.message : "Request failed", "error");
      await loadEmailChangeEligibility();
    } finally {
      setEmailChangeBusy(false);
    }
  }, [authToken, pushToast, loadEmailChangeEligibility]);

  const handleRequestMobileChange = useCallback(async () => {
    if (!isApiConfigured()) {
      pushToast("Connect the app to the API to change your mobile number.", "error");
      return;
    }
    const t = String(authToken || "").trim();
    if (!t) {
      pushToast("Sign in again to change your mobile number.", "error");
      return;
    }
    setMobileChangeBusy(true);
    try {
      const data = await postRequestMobileChange(t);
      if (data.skipped) {
        pushToast(data.message || "Email could not be sent.", "error");
      } else if (data.sent) {
        if (data.eligibility) {
          setMobileChangeEligibility(data.eligibility);
        } else {
          await loadMobileChangeEligibility();
        }
        pushToast("Check your email for the link to continue.", "success");
      } else {
        pushToast(data.message || "We could not send the email. Try again shortly.", "error");
      }
    } catch (err) {
      pushToast(err instanceof Error ? err.message : "Request failed", "error");
      await loadMobileChangeEligibility();
    } finally {
      setMobileChangeBusy(false);
    }
  }, [authToken, pushToast, loadMobileChangeEligibility]);

  const emailChangeHintBlock = useMemo(() => {
    if (!emailChangeEligibility || emailChangeEligibility.canRequest) return null;
    const support = (
      <a className="profile-manage-email-change-mailto" href="mailto:help@jobuwin.com">
        help@jobuwin.com
      </a>
    );
    if (emailChangeEligibility.lockedUntil) {
      return (
        <p className="profile-manage-email-change-hint">
          You have reached the maximum number of email change link requests. This option will be available again after{" "}
          <strong>{formatLocaleDateTime(emailChangeEligibility.lockedUntil)}</strong>. If you need more support, please email us at{" "}
          {support}.
        </p>
      );
    }
    if (emailChangeEligibility.cooldownEndsAt) {
      return (
        <p className="profile-manage-email-change-hint">
          We sent an email to your current email to initiate an email change. If you need more support, please email us at{" "}
          {support}. You can request another link after{" "}
          <strong>{formatLocaleDateTime(emailChangeEligibility.cooldownEndsAt)}</strong>.
        </p>
      );
    }
    return null;
  }, [emailChangeEligibility]);

  const addDefaultApplyCountryCode = useCallback((iso) => {
    const c = String(iso || "").trim().toUpperCase();
    if (!ISO_SET.has(c)) return;
    setDefaultApplyCountryCodes((prev) => {
      if (prev.includes(c)) return prev;
      if (prev.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) return prev;
      return [...prev, c];
    });
    setDefaultApplyCountriesInputDraft("");
  }, []);

  const removeDefaultApplyCountryCode = useCallback((iso) => {
    setDefaultApplyCountryCodes((prev) => prev.filter((x) => x !== iso));
  }, []);

  const addDefaultApplyCountryGroup = useCallback((groupCodes) => {
    const list = [
      ...new Set(
        (groupCodes || [])
          .map((c) => String(c || "").trim().toUpperCase())
          .filter((c) => ISO_SET.has(c))
      ),
    ];
    if (!list.length) return;
    setDefaultApplyCountryCodes((prev) => {
      const seen = new Set(prev);
      const out = [...prev];
      for (const c of list) {
        if (seen.has(c)) continue;
        if (out.length >= MAX_DEFAULT_APPLY_COUNTRY_CODES) break;
        seen.add(c);
        out.push(c);
      }
      return out;
    });
    setDefaultApplyCountriesInputDraft("");
  }, []);

  const handleSaveRecruitmentSettings = async (event) => {
    event.preventDefault();
    const token = String(authToken || "").trim();
    if (!isHiringRole(currentUser)) {
      pushToast("Sign in with a hiring account to save recruitment settings.", "error");
      return;
    }
    if (defaultApplyAudience === "countries" && defaultApplyCountryCodes.length === 0) {
      pushToast("Select at least one country, or choose “Anyone on JOBUWIN”.", "error");
      return;
    }
    const canPersistToApi = isApiConfigured() && token;
    const patchPayload = {
      defaultApplyAudience,
      defaultApplyCountryCodes: defaultApplyAudience === "anyone" ? [] : defaultApplyCountryCodes,
      defaultAdvertisingCurrency: defaultAdvertisingCurrency === "" ? null : defaultAdvertisingCurrency,
      defaultJobCountry: defaultJobCountry === "" ? null : defaultJobCountry,
      defaultJobTownCity: defaultJobTownCity.trim() === "" ? null : defaultJobTownCity,
    };
    if (canPersistToApi) {
      try {
        const { user } = await patchHiringRecruitmentDefaults(patchPayload, token);
        setCurrentUser((prev) => ({
          ...prev,
          ...user,
          recruitmentProfile: {
            ...(prev.recruitmentProfile && typeof prev.recruitmentProfile === "object"
              ? prev.recruitmentProfile
              : {}),
            ...(user.recruitmentProfile && typeof user.recruitmentProfile === "object"
              ? user.recruitmentProfile
              : {}),
          },
        }));
        setDefaultApplyCountriesInputDraft("");
        pushToast("Recruitment settings saved.", "success");
        return;
      } catch (err) {
        pushToast(err instanceof Error ? err.message : "Could not save recruitment settings.", "error");
        return;
      }
    }
    setCurrentUser((prev) => ({
      ...prev,
      recruitmentProfile: {
        ...(prev.recruitmentProfile && typeof prev.recruitmentProfile === "object"
          ? prev.recruitmentProfile
          : {}),
        ...patchPayload,
      },
    }));
    setDefaultApplyCountriesInputDraft("");
    pushToast("Recruitment settings saved.", "success");
  };

  const mobileChangeHintBlock = useMemo(() => {
    if (!mobileChangeEligibility || mobileChangeEligibility.canRequest) return null;
    const support = (
      <a className="profile-manage-email-change-mailto" href="mailto:help@jobuwin.com">
        help@jobuwin.com
      </a>
    );
    if (mobileChangeEligibility.lockedUntil) {
      return (
        <p className="profile-manage-email-change-hint">
          You have reached the maximum number of mobile change link requests. This option will be available again after{" "}
          <strong>{formatLocaleDateTime(mobileChangeEligibility.lockedUntil)}</strong>. If you need more support, please
          email us at {support}.
        </p>
      );
    }
    if (mobileChangeEligibility.cooldownEndsAt) {
      return (
        <p className="profile-manage-email-change-hint">
          We sent an email to your current email to initiate a mobile change. If you need more support, please email us
          at {support}. You can request another link after{" "}
          <strong>{formatLocaleDateTime(mobileChangeEligibility.cooldownEndsAt)}</strong>.
        </p>
      );
    }
    return null;
  }, [mobileChangeEligibility]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = String(authToken || "").trim();
    const canPersistToApi = isApiConfigured() && token && isHiringRole(currentUser);

    let photoUserPatch = null;
    let companyLogoUserPatch = null;
    if (canPersistToApi) {
      try {
        if (pendingPhotoFileRef.current) {
          const { user } = await postHiringProfilePhoto(pendingPhotoFileRef.current, token);
          pendingPhotoFileRef.current = null;
          photoUserPatch = user;
          if (photoInputRef.current) photoInputRef.current.value = "";
          setPhotoPreview(user.photoDataUrl || null);
        } else {
          const hadPersistedPhoto =
            currentUser?.photoDataUrl && !String(currentUser.photoDataUrl).startsWith("data:");
          const removingPersistedPhotoWithoutReplace = !photoPreview && hadPersistedPhoto;
          if (removingPersistedPhotoWithoutReplace) {
            const { user } = await deleteHiringProfilePhoto(token);
            photoUserPatch = user;
            setPhotoPreview(null);
          }
        }
        if (pendingCompanyLogoFileRef.current) {
          const { user } = await postHiringCompanyLogo(pendingCompanyLogoFileRef.current, token);
          pendingCompanyLogoFileRef.current = null;
          companyLogoUserPatch = user;
          if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
          setCompanyLogoPreview(user.companyLogoDataUrl || null);
        } else {
          const hadPersistedLogo =
            currentUser?.companyLogoDataUrl &&
            !String(currentUser.companyLogoDataUrl).startsWith("data:");
          const removingPersistedLogoWithoutReplace = !companyLogoPreview && hadPersistedLogo;
          if (removingPersistedLogoWithoutReplace) {
            const { user } = await deleteHiringCompanyLogo(token);
            companyLogoUserPatch = user;
            setCompanyLogoPreview(null);
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not update profile assets.";
        pushToast(msg, "error");
        return;
      }
    }

    const customs = customSocialRows
      .slice(0, MAX_CUSTOM_SOCIAL_LINKS)
      .map(({ id, platformName, url }) => ({
        id,
        platformName: (platformName || "").trim(),
        url: (url || "").trim(),
      }))
      .filter((row) => row.platformName || row.url);

    const socialProfiles = {
      linkedin: socialLinkedin.trim(),
      instagram: socialInstagram.trim(),
      youtube: socialYoutube.trim(),
      customs,
    };

    let socialUserPatch = null;
    if (canPersistToApi) {
      try {
        const { user } = await patchHiringSocialProfiles({ socialProfiles }, token);
        socialUserPatch = user;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not save social profiles.";
        pushToast(msg, "error");
        return;
      }
    }

    let operatingCountryUserPatch = null;
    const savedOperatingCountry = readOperatingCountryFromUser(currentUser);
    if (canPersistToApi && operatingCountry !== savedOperatingCountry) {
      if (!operatingCountry) {
        pushToast("Pick the country you operate from.", "error");
        return;
      }
      try {
        const { user } = await patchHiringRecruitmentDefaults({ defaultOperatingCountry: operatingCountry }, token);
        operatingCountryUserPatch = user;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not save your operating country.";
        pushToast(msg, "error");
        return;
      }
    }

    const rpApplySaved = readRecruitmentProfile(currentUser);
    const recruitmentProfile = {
      organizationName: organizationName.trim(),
      contactName: contactName.trim(),
      workEmail: workEmail.trim(),
      careersUrl: careersUrl.trim(),
      officePhoneDialCode: getDialCodeForCountryIso(officePhoneCountryIso),
      officePhoneNumber: officePhoneNumber.trim(),
      companyLogoDataUrl: companyLogoPreview || "",
      socialProfiles,
      defaultApplyAudience: rpApplySaved.defaultApplyAudience,
      defaultApplyCountryCodes: rpApplySaved.defaultApplyCountryCodes,
      defaultAdvertisingCurrency: readDefaultAdvertisingCurrency(rpApplySaved),
    };

    const mergedPhotoUrl = photoUserPatch
      ? photoUserPatch.photoDataUrl || undefined
      : photoPreview || undefined;
    const mergedCompanyLogoUrl = companyLogoUserPatch
      ? companyLogoUserPatch.companyLogoDataUrl || undefined
      : companyLogoPreview || undefined;

    setCurrentUser((prev) => ({
      ...prev,
      ...(photoUserPatch || {}),
      ...(companyLogoUserPatch || {}),
      ...(socialUserPatch || {}),
      ...(operatingCountryUserPatch || {}),
      name: recruitmentProfile.contactName || prev.name,
      email: recruitmentProfile.workEmail || prev.email,
      photoDataUrl: mergedPhotoUrl,
      socialProfiles,
      companyLogoDataUrl: mergedCompanyLogoUrl,
      recruitmentProfile,
    }));
    if (operatingCountryUserPatch) {
      setOperatingCountry(readOperatingCountryFromUser(operatingCountryUserPatch));
    }
    pushToast("Recruitment profile saved.", "success");
  };

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-recruitment-profile-main jw-recruitment-main recruitment-profile-page">
        <div className="recruitment-profile-shell nd1-recruitment-profile-shell">
          <header className="recruitment-profile-masthead nd1-recruitment-profile-masthead">
            <Link className="back-link nd1-recruitment-profile-back" to="/hiring-dashboard">
              &larr; Back to recruitment dashboard
            </Link>
            <p className="eyebrow nd1-label nd1-recruitment-profile-eyebrow">Your organization</p>
            <h1>Manage recruitment profile</h1>
            <RecruitmentProfileSettingsNav active={activeSettingsTab} onSelectTab={selectSettingsTab} />
          </header>

          <div className="recruitment-profile-content profile-manage-card nd1-recruitment-profile-content">
          {activeSettingsTab === "profile" ? (
          <>
          <form
            id="recruitment-profile-manage-form"
            className="profile-manage-form recruitment-profile-form"
            onSubmit={handleSubmit}
          >
            <div className="recruitment-profile-stack">
              <div className="nd1-recruitment-profile-brand-row">
              <div className="recruitment-profile-surface nd1-recruitment-profile-brand-card">
              <div className="profile-manage-photo-column">
                <p className="profile-manage-section-title">Profile photo</p>
                <div className="profile-manage-photo-panel">
                  <div className="profile-manage-photo-panel-body">
                    <div className="profile-manage-photo-preview-wrap">
                      <img
                        src={displayPhoto}
                        alt=""
                        className={`profile-manage-photo-preview${photoPreview ? "" : " profile-manage-photo-preview--placeholder"}`}
                        width={96}
                        height={96}
                      />
                      {photoPreview ? (
                        <button
                          type="button"
                          className="nd1-recruitment-profile-preview-delete-btn"
                          onClick={handleProfilePhotoDelete}
                          aria-label="Remove profile photo"
                        >
                          <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="profile-manage-photo-panel-footer">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="profile-manage-file-input"
                      onChange={onPhotoChange}
                      aria-label="Choose profile photo"
                    />
                    <button
                      type="button"
                      className="small-outline-button profile-manage-panel-action-btn nd1-recruitment-profile-upload-btn"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                      {photoPreview ? "Change photo" : "Upload photo"}
                    </button>
                  </div>
                  <p className="profile-manage-helper profile-manage-photo-limit-hint">
                    JPEG, PNG, or WebP — up to {PROFILE_PHOTO_MAX_MB} MB.
                  </p>
                </div>
              </div>
              </div>

              <div className="recruitment-profile-surface nd1-recruitment-profile-brand-card">
              <div className="profile-manage-cv-column">
                <p className="profile-manage-section-title">Company logo</p>
                <div className="profile-manage-photo-panel profile-manage-company-logo-panel">
                  <div className="profile-manage-photo-panel-body">
                    <div className="profile-manage-photo-preview-wrap">
                      <img
                        src={displayCompanyLogo}
                        alt=""
                        className={`profile-manage-company-logo-preview${
                          companyLogoPreview ? "" : " profile-manage-company-logo-preview--placeholder"
                        }`}
                        width={104}
                        height={104}
                      />
                      {companyLogoPreview ? (
                        <button
                          type="button"
                          className="nd1-recruitment-profile-preview-delete-btn"
                          onClick={handleCompanyLogoDelete}
                          aria-label="Remove company logo"
                        >
                          <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="profile-manage-photo-panel-footer profile-manage-company-logo-footer">
                    <input
                      ref={companyLogoInputRef}
                      type="file"
                      accept="image/*"
                      className="profile-manage-file-input"
                      onChange={onCompanyLogoChange}
                      aria-label="Choose company logo"
                    />
                    <button
                      type="button"
                      className="small-outline-button profile-manage-panel-action-btn nd1-recruitment-profile-upload-btn"
                      onClick={() => companyLogoInputRef.current?.click()}
                    >
                      <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                      {companyLogoPreview ? "Change logo" : "Upload logo"}
                    </button>
                  </div>
                  <p className="profile-manage-helper profile-manage-photo-limit-hint">
                    JPEG, PNG, or WebP — up to {PROFILE_PHOTO_MAX_MB} MB.
                  </p>
                </div>
              </div>
              </div>
              </div>

              <div className="recruitment-profile-surface">
                <p className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading">
                  <Building2 className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Organization &amp; contact
                </p>

                <div className="profile-manage-form-field">
                  <label htmlFor="recruitOrgName">Company or organization name</label>
                  <input
                    id="recruitOrgName"
                    name="recruitOrgName"
                    type="text"
                    autoComplete="organization"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                  />
                </div>

                <div className="profile-manage-form-field">
                  <label htmlFor="recruitCareersUrl">Company website</label>
                  <div className="profile-manage-url-field">
                    <span className="profile-manage-url-prefix">https://</span>
                    <input
                      id="recruitCareersUrl"
                      name="recruitCareersUrl"
                      type="text"
                      inputMode="url"
                      autoComplete="url"
                      placeholder="company.com"
                      value={careersUrl}
                      onChange={(e) => setCareersUrl(stripHttpScheme(e.target.value))}
                    />
                  </div>
                </div>

                <div className="profile-manage-form-field">
                  <label htmlFor="recruitContactName">Contact name</label>
                  <input
                    id="recruitContactName"
                    name="recruitContactName"
                    type="text"
                    autoComplete="name"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    required
                  />
                </div>

                <div className="profile-manage-form-field">
                  <label htmlFor="recruitOfficePhone">Mobile number</label>
                  <div className="mobile-row">
                    <select
                      id="recruitOfficePhoneCountry"
                      name="recruitOfficePhoneCountry"
                      className="profile-manage-field--locked"
                      value={officePhoneCountryIso}
                      disabled
                      aria-label="Country calling code (cannot be changed here)"
                      title="Mobile country code cannot be changed on this page"
                    >
                      {COUNTRY_DIAL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <input
                      id="recruitOfficePhone"
                      name="recruitOfficePhone"
                      className="profile-manage-field--locked"
                      type="tel"
                      autoComplete="tel"
                      value={officePhoneNumber}
                      placeholder="7123 456789"
                      disabled
                      title="Mobile number cannot be changed on this page"
                    />
                  </div>
                </div>

                <div className="profile-manage-form-field">
                  <label htmlFor="recruitWorkEmail">Email address</label>
                  <input
                    id="recruitWorkEmail"
                    name="recruitWorkEmail"
                    className="profile-manage-field--locked"
                    type="email"
                    autoComplete="email"
                    value={workEmail}
                    disabled
                    title="Work email cannot be changed on this page"
                  />
                </div>

                <div className="profile-manage-reset-password-wrap nd1-recruitment-profile-account-actions">
                  <button
                    type="button"
                    className="small-outline-button profile-manage-reset-password-btn nd1-recruitment-profile-account-btn"
                    onClick={() =>
                      navigate("/signin", {
                        state: { passwordReset: true, emailHint: workEmail.trim() || undefined },
                      })
                    }
                  >
                    <Lock size={16} strokeWidth={1.75} aria-hidden="true" />
                    Reset password
                  </button>
                  <button
                    type="button"
                    className="small-outline-button profile-manage-reset-password-btn nd1-recruitment-profile-account-btn"
                    disabled={
                      emailChangeBusy || Boolean(emailChangeEligibility && !emailChangeEligibility.canRequest)
                    }
                    onClick={handleRequestEmailChange}
                  >
                    <Mail size={16} strokeWidth={1.75} aria-hidden="true" />
                    {emailChangeBusy ? "Sending…" : "Change email"}
                  </button>
                  <button
                    type="button"
                    className="small-outline-button profile-manage-reset-password-btn nd1-recruitment-profile-account-btn"
                    disabled={
                      mobileChangeBusy || Boolean(mobileChangeEligibility && !mobileChangeEligibility.canRequest)
                    }
                    onClick={handleRequestMobileChange}
                  >
                    <Smartphone size={16} strokeWidth={1.75} aria-hidden="true" />
                    {mobileChangeBusy ? "Sending…" : "Change mobile"}
                  </button>
                </div>
                {emailChangeHintBlock || mobileChangeHintBlock ? (
                  <div className="profile-manage-email-change-hint-wrap profile-manage-account-change-hint-wrap">
                    {emailChangeHintBlock}
                    {mobileChangeHintBlock}
                  </div>
                ) : null}
              </div>

              <div className="recruitment-profile-surface recruitment-profile-surface--operating-country">
                <p className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading">
                  <Globe className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Country you operate from
                </p>
                <p className="profile-manage-helper">
                  We use this for pricing and billing. Changing country updates your pricing currency — it
                  won&apos;t change your job advertising currency below.
                </p>
                <div className="profile-manage-form-field">
                  <label htmlFor="profile-operating-country">Country</label>
                  <select
                    id="profile-operating-country"
                    value={operatingCountry}
                    onChange={(e) =>
                      setOperatingCountry(String(e.target.value || "").trim().toUpperCase())
                    }
                  >
                    <option value="">Please select</option>
                    {COUNTRIES.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="recruitment-profile-surface recruitment-profile-surface--social">
            <div className="profile-manage-social-section">
              <p className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading">
                <Link2 className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                Company social profiles
              </p>

              <div className="profile-manage-social-row profile-manage-social-row--brand">
                <SocialBrandIcon network="linkedin" href={socialUrlForLink(socialLinkedin)} />
                <label htmlFor="recruitSocialLinkedin">LinkedIn</label>
                <div className="profile-manage-url-field">
                  <span className="profile-manage-url-prefix">https://</span>
                  <input
                    id="recruitSocialLinkedin"
                    name="recruitSocialLinkedin"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="linkedin.com/company/your-org"
                    value={socialLinkedin}
                    onChange={(e) => setSocialLinkedin(stripHttpScheme(e.target.value))}
                  />
                </div>
              </div>

              <div className="profile-manage-social-row profile-manage-social-row--brand">
                <SocialBrandIcon network="instagram" href={socialUrlForLink(socialInstagram)} />
                <label htmlFor="recruitSocialInstagram">Instagram</label>
                <div className="profile-manage-url-field">
                  <span className="profile-manage-url-prefix">https://</span>
                  <input
                    id="recruitSocialInstagram"
                    name="recruitSocialInstagram"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="instagram.com/yourcompany"
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(stripHttpScheme(e.target.value))}
                  />
                </div>
              </div>

              <div className="profile-manage-social-row profile-manage-social-row--brand">
                <SocialBrandIcon network="youtube" href={socialUrlForLink(socialYoutube)} />
                <label htmlFor="recruitSocialYoutube">YouTube</label>
                <div className="profile-manage-url-field">
                  <span className="profile-manage-url-prefix">https://</span>
                  <input
                    id="recruitSocialYoutube"
                    name="recruitSocialYoutube"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="youtube.com/@yourchannel"
                    value={socialYoutube}
                    onChange={(e) => setSocialYoutube(stripHttpScheme(e.target.value))}
                  />
                </div>
              </div>

              {customSocialRows.map((row, index) => (
                <div key={row.id} className="profile-manage-social-custom-entry">
                  <div className="profile-manage-social-custom-grid">
                    <label className="social-custom-name-label" htmlFor={`recruit-social-name-${row.id}`}>
                      Platform name ({index + 1}/{MAX_CUSTOM_SOCIAL_LINKS})
                    </label>
                    <input
                      id={`recruit-social-name-${row.id}`}
                      className="social-custom-name-input"
                      type="text"
                      name={`recruitSocialCustomName-${row.id}`}
                      autoComplete="off"
                      placeholder="e.g. X, GitHub, Behance"
                      value={row.platformName}
                      onChange={(e) => updateCustomSocialRow(index, "platformName", e.target.value)}
                    />
                    <button
                      type="button"
                      className="profile-manage-social-delete"
                      onClick={() => removeCustomSocialRow(index)}
                      aria-label="Remove this custom profile"
                    >
                      <svg
                        className="profile-manage-social-delete-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14M10 11v6M14 11v6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <label className="social-custom-url-label" htmlFor={`recruit-social-url-${row.id}`}>
                      Link
                    </label>
                    <div className="social-custom-url-input profile-manage-url-field">
                      <span className="profile-manage-url-prefix">https://</span>
                      <input
                        id={`recruit-social-url-${row.id}`}
                        type="text"
                        inputMode="url"
                        autoComplete="url"
                        name={`recruitSocialCustomUrl-${row.id}`}
                        placeholder="your-link.com"
                        value={row.url}
                        onChange={(e) =>
                          updateCustomSocialRow(index, "url", stripHttpScheme(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="profile-manage-add-custom-social-wrap nd1-recruitment-profile-add-social-wrap">
                <button
                  type="button"
                  className="nd1-recruitment-profile-add-social-btn"
                  onClick={addCustomSocialRow}
                  aria-label="Add another social profile link"
                  disabled={customSocialRows.length >= MAX_CUSTOM_SOCIAL_LINKS}
                >
                  <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
                  Add another link
                </button>
                <p className="profile-manage-custom-social-counter nd1-recruitment-profile-add-social-counter">
                  {customSocialRows.length}/{MAX_CUSTOM_SOCIAL_LINKS}
                </p>
              </div>
            </div>
              </div>
            </div>

          </form>
          <div
            className={`job-actions-float nd1-recruitment-profile-footer${isProfileDirty ? " job-actions-float--unsaved" : ""}`}
            role="toolbar"
            aria-label={
              isProfileDirty ? "Profile save actions, unsaved changes" : "Profile save actions"
            }
          >
            {isProfileDirty ? (
              <p className="profile-manage-unsaved-hint" role="status">
                Changes are not saved.
              </p>
            ) : null}
            <div className="job-actions-float-row">
              <div className="job-actions-buttons">
                <button
                  type="submit"
                  form="recruitment-profile-manage-form"
                  className="job-action-btn primary"
                  disabled={!isProfileDirty}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="job-action-btn"
                  disabled={!isProfileDirty}
                  aria-label="Discard unsaved changes and restore last saved profile"
                  onClick={resetRecruitmentFormToSaved}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="job-action-btn"
                  onClick={() => navigate("/hiring-dashboard")}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          </>
          ) : (
            <>
              <form
                id="recruitment-settings-form"
                className="profile-manage-form recruitment-settings-form recruitment-profile-form"
                onSubmit={handleSaveRecruitmentSettings}
              >
                <div className="recruitment-profile-stack recruitment-profile-stack--settings">
                <div className="recruitment-profile-surface nd1-recruitment-settings-intro">
                  <p className="nd1-recruitment-settings-intro-text">
                    Set your defaults here — things like who can apply and your usual job location. You can still
                    override any of these when you create a new role.
                  </p>
                </div>

                <fieldset
                  className="profile-manage-field-group recruitment-default-apply-fieldset recruitment-profile-surface nd1-recruitment-settings-fieldset"
                  aria-labelledby="recruitment-default-apply-legend"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                    id="recruitment-default-apply-legend"
                  >
                    <Users className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    Who can apply for jobs by default?
                  </p>
                  <div
                    className="recruitment-default-apply-radios"
                    role="radiogroup"
                    aria-labelledby="recruitment-default-apply-legend"
                  >
                    <label className="recruitment-default-apply-radio">
                      <input
                        type="radio"
                        className="checkbox-control checkbox-control--primary-2"
                        name="recruitment-default-apply-audience"
                        checked={defaultApplyAudience === "anyone"}
                        onChange={() => {
                          setDefaultApplyAudience("anyone");
                          setDefaultApplyCountriesInputDraft("");
                        }}
                      />
                      <span>Anyone on JOBUWIN</span>
                    </label>
                    <label className="recruitment-default-apply-radio">
                      <input
                        type="radio"
                        className="checkbox-control checkbox-control--primary-2"
                        name="recruitment-default-apply-audience"
                        checked={defaultApplyAudience === "countries"}
                        onChange={() => setDefaultApplyAudience("countries")}
                      />
                      <span>People from specific countries</span>
                    </label>
                  </div>
                  {defaultApplyAudience === "countries" ? (
                    <div className="profile-manage-country-select-group profile-manage-country-field recruitment-default-apply-countries">
                      <label
                        className="profile-manage-country-pills-field-label"
                        htmlFor="recruitment-default-apply-countries-input"
                      >
                        Countries ({defaultApplyCountryCodes.length}/{MAX_DEFAULT_APPLY_COUNTRY_CODES})
                      </label>
                      <div className="recruitment-default-apply-picker-stack">
                        <WorkCountriesPillPicker
                          disabled={false}
                          countryCodes={defaultApplyCountryCodes}
                          inputDraft={defaultApplyCountriesInputDraft}
                          onDraftChange={setDefaultApplyCountriesInputDraft}
                          onAddCode={addDefaultApplyCountryCode}
                          onRemoveCode={removeDefaultApplyCountryCode}
                          maxCodes={MAX_DEFAULT_APPLY_COUNTRY_CODES}
                          inputId="recruitment-default-apply-countries-input"
                          placeholderDisabled="Search countries"
                        />
                        {EEA_COUNTRY_GROUP ? (
                          <div
                            className="profile-manage-country-group-shortcuts recruitment-default-apply-eea-shortcut"
                            role="group"
                            aria-label="Add all countries from a predefined group"
                          >
                            <button
                              type="button"
                              className="profile-manage-country-group-shortcut-link"
                              title={`Add: ${EEA_COUNTRY_GROUP.countryCodes.join(", ")}`}
                              onClick={() => addDefaultApplyCountryGroup(EEA_COUNTRY_GROUP.countryCodes)}
                            >
                              <span className="profile-manage-country-group-shortcut-link__plus" aria-hidden="true">
                                +
                              </span>
                              <span className="profile-manage-country-group-shortcut-link__label">
                                {EEA_COUNTRY_GROUP.label}
                              </span>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset
                  className="profile-manage-field-group recruitment-default-currency-fieldset recruitment-profile-surface nd1-recruitment-settings-fieldset"
                  aria-labelledby="recruitment-default-currency-legend"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                    id="recruitment-default-currency-legend"
                  >
                    <Coins className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    Default job advertising currency
                  </p>
                  <label className="job-create-salary-currency recruitment-default-currency-label">
                    <select
                      id="recruitment-default-account-currency"
                      value={defaultAdvertisingCurrency}
                      onChange={(e) => setDefaultAdvertisingCurrency(e.target.value)}
                      aria-label="Default job advertising currency"
                    >
                      <option value="">Please select</option>
                      {SALARY_CURRENCY_OPTIONS.map(({ value, symbol, name }) => (
                        <option key={value} value={value}>
                          {symbol} {value} — {name}
                        </option>
                      ))}
                    </select>
                  </label>
                </fieldset>

                <fieldset
                  className="profile-manage-field-group recruitment-default-location-fieldset recruitment-profile-surface nd1-recruitment-settings-fieldset"
                  aria-labelledby="recruitment-default-location-legend"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                    id="recruitment-default-location-legend"
                  >
                    <MapPin className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    Default job location
                  </p>
                  <p className="profile-manage-helper recruitment-default-location-helper">
                    We&apos;ll pre-fill these when you create a new job. You can still change them for each role.
                  </p>
                  <div className="job-create-grid-2 recruitment-default-location-grid">
                    <label className="recruitment-default-location-field">
                      <span className="job-search-label">Country</span>
                      <select
                        id="recruitment-default-job-country"
                        value={defaultJobCountry}
                        onChange={(e) => setDefaultJobCountry(String(e.target.value || "").trim().toUpperCase())}
                      >
                        <option value="">Please select</option>
                        {COUNTRIES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="recruitment-default-location-field">
                      <span className="job-search-label">Town/City</span>
                      <input
                        id="recruitment-default-job-towncity"
                        type="text"
                        placeholder="e.g. London"
                        value={defaultJobTownCity}
                        onChange={(e) => setDefaultJobTownCity(e.target.value)}
                        maxLength={120}
                      />
                    </label>
                  </div>
                </fieldset>
                </div>
              </form>
              <div
                className={`job-actions-float nd1-recruitment-profile-footer${isRecruitmentSettingsDirty ? " job-actions-float--unsaved" : ""}`}
                role="toolbar"
                aria-label={
                  isRecruitmentSettingsDirty
                    ? "Recruitment settings save actions, unsaved changes"
                    : "Recruitment settings save actions"
                }
              >
                {isRecruitmentSettingsDirty ? (
                  <p className="profile-manage-unsaved-hint" role="status">
                    Changes are not saved.
                  </p>
                ) : null}
                <div className="job-actions-float-row">
                  <div className="job-actions-buttons">
                    <button
                      type="submit"
                      form="recruitment-settings-form"
                      className="job-action-btn primary"
                      disabled={!isRecruitmentSettingsDirty}
                    >
                      Save changes
                    </button>
                    <button
                      type="button"
                      className="job-action-btn"
                      disabled={!isRecruitmentSettingsDirty}
                      aria-label="Discard unsaved recruitment settings"
                      onClick={resetRecruitmentSettingsToSaved}
                    >
                      Reset
                    </button>
                    <button type="button" className="job-action-btn" onClick={() => navigate("/hiring-dashboard")}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </main>
      {toast ? (
        <div
          className={`profile-save-toast profile-save-toast--${toast.tone}`}
          role={toast.tone === "error" ? "alert" : "status"}
        >
          <p className="profile-save-toast__message">{toast.message}</p>
          <button type="button" className="profile-save-toast__close" onClick={dismissToast}>
            Close
          </button>
        </div>
      ) : null}
    </RecruitmentShell>
  );
}

export default RecruitmentProfilePage;
